import { Component, Input } from '@angular/core'
import { CostInvoicePatch, CostInvoiceRead, InvoicesService, PaymentStatusRead } from '@app/generated'
import { FormControl, FormGroup } from '@angular/forms'
import { NotificationService } from '@core/services/notification.service'
import { ModalService } from '@core/services/modal.service'
import { LoaderService } from '@core/services/loader.service'
import { finalize } from 'rxjs'
import { getItemTranslation } from '@app/general/utils/get-item-translation'

@Component({
	selector: 'app-cost-invoice-update-status',
	templateUrl: './cost-invoice-update-status.component.html',
	styleUrl: './cost-invoice-update-status.component.scss',
})
export class CostInvoiceUpdateStatusComponent {
	@Input() costInvoice: CostInvoiceRead = {} as CostInvoiceRead
	@Input() statuses: PaymentStatusRead[] = []
	isSubmitting: boolean = false
	statusForm = {
		formGroup: new FormGroup({
			status: new FormControl<PaymentStatusRead | null>(null),
			isPaidCash: new FormControl<boolean>(false),
		}),
	}
	protected readonly getItemTranslation = getItemTranslation
	protected readonly confirmButtonText = $localize`Confirm`
	protected readonly cancelButtonText = $localize`Cancel`
	protected readonly textPaidWithCash = $localize`Paid with cash`

	constructor(
		private notificationService: NotificationService,
		private modalService: ModalService,
		private loaderService: LoaderService,
		private invoiceService: InvoicesService,
	) {}

	ngOnInit() {
		if (!this.statuses?.length) {
			this.fetchStatuses()
		}
		this.statusForm.formGroup.patchValue({
			status: this.costInvoice.payment_status,
			isPaidCash: this.costInvoice.is_paid_cash ?? false,
		})
	}

	fetchStatuses() {
		this.invoiceService.getPaymentStatuses().subscribe({
			next: (response: PaymentStatusRead[]) => {
				this.statuses = response
			},
			error: error => {
				let message = $localize`Was not able to fetch statuses`
				if (error.code === 422) {
					message = $localize`Form contains errors`
				}
				this.notificationService.error(message)
			},
		})
	}

	cancelUpdateStatus() {
		this.modalService.modalRef.close(false)
	}

	patchData() {
		if (this.isSubmitting) {
			return
		}

		const path: CostInvoicePatch = {
			payment_status_id: this.statusForm.formGroup.controls.status.value!.id!,
			is_paid_cash:
				((this.statusForm.formGroup.controls.status.value?.is_partially_paid ||
					this.statusForm.formGroup.controls.status.value?.is_paid) &&
					this.statusForm.formGroup.controls.isPaidCash.value) ??
				false,
		}
		if (this.costInvoice.id) {
			this.isSubmitting = true
			this.loaderService.enable()
			this.invoiceService
				.patchCostInvoice(this.costInvoice.id, path)
				.pipe(
					finalize(() => {
						this.loaderService.disable()
						this.isSubmitting = false
					}),
				)
				.subscribe({
					next: (response: CostInvoiceRead) => {
						this.modalService.modalRef.close(response)
						this.notificationService.success($localize`Cost invoice payment status has been updated`)
					},
					error: () => {
						this.notificationService.error($localize`Failed to update cost invoice status`)
					},
				})
		}
	}
}
